"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "../../lib/cn";
import { INVALID_FIELD, ValidCheck } from "./field-shell";
import {
  DIAL_CODES,
  dialCodeFor,
  dialCodeForNumber,
  matchesDialQuery,
  type DialCode,
} from "../../lib/booking/dial-codes";

/** Telefonfeld mit Vorwahl-Dropdown. Die Liste deckt ganz Europa (+ USA,
 *  China) ab, die Kernmärkte stehen oben, darunter alphabetisch — mit
 *  Suchfeld über Name, Endonym, ISO-Code und Vorwahl. Der gespeicherte
 *  Wert ist immer "+49 171 …" (Vorwahl + Leerzeichen + lokaler Teil). */
export function PhoneInput({
  id,
  value,
  onChange,
  defaultCountry = "DE",
  placeholder = "Telefonnummer",
  invalid = false,
  valid = false,
}: {
  /** Forwarded onto the tel input so a FloatField <label htmlFor> can focus it. */
  id?: string;
  value: string;
  onChange: (v: string) => void;
  defaultCountry?: string;
  placeholder?: string;
  invalid?: boolean;
  /** Green check inside the field — live positive feedback. */
  valid?: boolean;
}) {
  const t = useTranslations("Booking");
  const guess = dialCodeFor(defaultCountry) ?? DIAL_CODES[0];
  const initial = dialCodeForNumber(value)?.code ?? guess.code;

  const [country, setCountry] = useState(initial);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  // Open upward when there isn't room below (the phone field sits low on
  // Step 2, just above the fixed bottom bar — a downward list would spill
  // behind it). Decided at open-time from the trigger's viewport position.
  const [dropUp, setDropUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep the active row in view while arrowing through a long list.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function close() {
    setOpen(false);
    setQuery("");
    setActive(0);
  }

  function toggleOpen() {
    if (open) {
      close();
      return;
    }
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // List is max-h-72 (288px) + search row + ~80px bottom bar clearance.
      setDropUp(spaceBelow < 380);
    }
    setOpen(true);
  }

  // Re-derive when the address country shifts (only while nothing typed).
  useEffect(() => {
    if (!defaultCountry || value) return;
    const next = dialCodeFor(defaultCountry);
    if (next) setCountry(next.code);
  }, [defaultCountry, value]);

  const current: DialCode = dialCodeFor(country) ?? DIAL_CODES[0];
  // Strip ONLY a leading dial prefix — never an occurrence inside the number.
  const local = value.startsWith(current.dial)
    ? value.slice(current.dial.length).trim()
    : value.replace(/^\+\d+\s*/, "").trim();

  function set(newDial: string, newLocal: string) {
    onChange(`${newDial} ${newLocal}`.trim());
  }

  function pick(d: DialCode) {
    setCountry(d.code);
    set(d.dial, local);
    close();
  }

  const filtered = DIAL_CODES.filter((d) => matchesDialQuery(d, query));
  // Visual divider between the pinned core markets and the alphabetical rest
  // (only when nothing is being searched — a filtered list is flat).
  const dividerIndex = query ? -1 : filtered.findIndex((d) => !d.pinned);

  function onSearchKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && filtered[active]) {
      e.preventDefault();
      pick(filtered[active]);
    }
  }

  return (
    <div ref={ref} className="relative">
      <div
        className={cn(
          "flex h-11 items-stretch overflow-hidden rounded-md border bg-white",
          invalid ? INVALID_FIELD : "border-line-200 focus-within:border-ink-900",
        )}
      >
        <button
          type="button"
          onClick={toggleOpen}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`${current.name} ${current.dial}`}
          className="inline-flex shrink-0 items-center gap-1.5 border-r border-line-200 px-2 text-sm font-medium text-ink-900 hover:bg-soft"
        >
          <span className={`fi fi-${current.iso} fis !rounded-sm`} aria-hidden />
          <span className="tabnums text-xs">{current.dial}</span>
          <ChevronDown className="h-3 w-3 text-ink-500" aria-hidden />
        </button>
        <input
          id={id}
          value={local}
          onChange={(e) => set(current.dial, e.target.value)}
          placeholder={placeholder}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className="flex-1 bg-transparent px-3 py-2 text-sm tabnums focus:outline-none"
        />
        {valid && !invalid ? (
          <span className="pointer-events-none flex items-center pr-3">
            <ValidCheck />
          </span>
        ) : null}
      </div>

      {open ? (
        <div
          className={cn(
            // z-50 so it sits ABOVE the fixed bottom action bar (z-40).
            "absolute left-0 z-50 w-72 overflow-hidden rounded-md border border-line-200 bg-white shadow-lg",
            dropUp ? "bottom-full mb-1" : "top-full mt-1",
          )}
        >
          <div className="flex items-center gap-2 border-b border-line-200 px-2.5 py-1.5">
            <Search className="h-3 w-3 text-ink-400" aria-hidden />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onSearchKey}
              placeholder={t("countrySearch")}
              className="flex-1 bg-transparent text-base placeholder:text-ink-400 focus:outline-none md:text-[13px]"
            />
          </div>
          <ul ref={listRef} role="listbox" className="max-h-72 overflow-auto py-1">
            {filtered.map((d, i) => (
              <li
                key={d.code}
                role="option"
                aria-selected={d.code === country}
                className={cn(i === dividerIndex && i > 0 && "mt-1 border-t border-line-200 pt-1")}
              >
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(d);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[13px] transition-colors",
                    i === active ? "bg-soft" : "bg-white",
                    d.code === country && "font-semibold",
                  )}
                >
                  <span className={`fi fi-${d.iso} fis !rounded-sm h-3 w-3.5`} aria-hidden />
                  <span className="flex-1 truncate text-ink-900">{d.name}</span>
                  <span className="text-[11px] text-ink-400 tabnums">{d.dial}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-[13px] text-ink-500">{t("noMatch")}</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
